/* eslint-disable @typescript-eslint/no-misused-promises */
// @ts-strict-ignore

import { FileDescriptor, FileState } from 'rainbow-web-sdk/models/common';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import type { FileStorageService } from 'rainbow-web-sdk/services/files/fileStorage.service';
import { FILE_STORAGE_SVC, FileStorageServiceEvents } from 'rainbow-web-sdk/services/files/fileStorage.service';
import type { FileServerService } from 'rainbow-web-sdk/services/files/fileServer.service';
import { FILE_SERVER_SVC } from 'rainbow-web-sdk/services/files/fileServer.service';

import xmlParser, { XMLElement, XMLElements } from 'rainbow-web-sdk/utils/xmlParser';
import { MainService } from '../core';

export class FileDescriptorEventHandlerService {

    private mainService: MainService = MainService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private fileStorageService: FileStorageService = this.mainService.inject<FileStorageService>(FILE_STORAGE_SVC);
    private fileServerService: FileServerService = this.mainService.inject<FileServerService>(FILE_SERVER_SVC);

    public static create(): FileDescriptorEventHandlerService {
        return new FileDescriptorEventHandlerService();
    }

    public onManagementMessageReceived(stanza: any) {
        try {
            const stanzaElem = xmlParser(stanza);
            if (stanzaElem.find("file").length > 0) this.manageFileStanzaData(stanzaElem.find("file"));
            else if (stanzaElem.find("thumbnail").length > 0) this.manageThumbnailStanzaData(stanzaElem.find("thumbnail"));
            else if (stanzaElem.find("archive").length && stanzaElem.find("fileid").length) this.manageArchiveFileReady(stanzaElem);
            return true;
        }
        catch (error) {
            this.logger.error("[FileDescriptorEventHandler] onManagementMessageReceived ERROR " + error);
            return true;
        }
    }

    private async manageArchiveFileReady(stanzaElem: XMLElement) {
        const action = stanzaElem.find("archive").attr("action");
        const reason = stanzaElem.find("archive").attr("reason");

        if (reason === 'archive_availability') {
            const fileId = stanzaElem.find('fileid').text();
            this.logger.info(`[FileDescriptorEventHandler] manageArchiveFileReady ${action} -- ${fileId}`);
            const fileDescriptor = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId, true);
            this.fileStorageService.sendEvent(FileStorageServiceEvents.ON_ARCHIVE_AVAILABLE, { fileDescriptor });
        }
    }

    private manageFileStanzaData(stanzaElem: XMLElements) {

        const action = stanzaElem.attr("action");

        // we might have many fileIds in the same message, we should treat them all one by one
        const filesId = stanzaElem.find("fileid");
        let updateConsumption = false;

        filesId.forEach(async (fileIdElem: XMLElement) => {
            const fileId = fileIdElem.text();
            const fileDescriptor: FileDescriptor = this.fileStorageService.getFileDescriptorById(fileId);

            this.logger.info("[FileDescriptorEventHandler] manageFileStanzaData -- " + action + " fileDescriptor " + fileId);

            switch (action) {
                case "create": {
                    this.logger.debug("[FileDescriptorEventHandler] fileDescriptor " + fileId + " created on server -- do nothing");
                    // if (fileDescriptor && fileDescriptor.recordingConferenceId) {
                    //     // Handle recording file descriptors (those are different than the 'regular' ones).
                    //     // The goal is to manage the cache of sharedWithMeRecordingFiles
                    //     // Should not interfere with the issue commented below
                    //     this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId);
                    //     this.logger.info("[FileDescriptorEventHandler] fileDescriptor retrieved");
                    // }
                    // CCH: I commented those lines below because they chang the state of the fileDescriptor at a bad time and break the circular progress
                    // const fileDesc = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId, true);
                    // this.logger.info("[FileDescriptorEventHandler] fileDescriptor retrieved");
                    // if (fileDesc.ownerId === this.contactService.userContact.dbId) { this.fileStorageService.retrieveUserConsumption(); }
                    break;
                }
                case "delete": {
                    const recordingFileDescriptor = this.fileStorageService.getConferenceRecordingFileById(fileId);
                    if (recordingFileDescriptor?.recordingConferenceId) {
                        this.fileStorageService.removeRecordFromRecordingFile(fileId, recordingFileDescriptor?.recordingConferenceId);
                    }
                    if (fileDescriptor) {
                        //check if we've deleted one of our own files
                        if (fileDescriptor.ownerId === this.contactService.userContact.dbId && fileDescriptor.state !== "deleted") {
                            updateConsumption = true;
                        }

                        this.fileStorageService.deleteFileDescriptorFromCache(fileId);
                        this.fileStorageService.deleteVoiceMessageFileDescriptor(fileId);
                        this.eventService.publish("ON_FILE_REMOVED_FROM_STORE_EVENT", { fileId: fileId });
                        this.fileStorageService.sendEvent(FileStorageServiceEvents.ON_FILE_REMOVED_FROM_STORE_EVENT, { fileId: fileId });

                    } else {
                        this.eventService.publish("ON_FILE_REMOVED_FROM_STORE_EVENT", { fileId: fileId });
                        this.fileStorageService.sendEvent(FileStorageServiceEvents.ON_FILE_REMOVED_FROM_STORE_EVENT, { fileId: fileId });
                    }
                    break;
                }
                case "update": {
                    try {
                        let unknownFile = false;
                        if (!this.fileStorageService.getFileDescriptorById(fileId)) {
                            unknownFile = true;
                        }

                        const fileDesc = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId, true);
                        this.logger.info("[FileDescriptorEventHandler] fileDescriptor retrieved");

                        //if file was added by myself but from another client, we should update the user consumption
                        if (fileDesc.ownerId === this.contactService.userContact.dbId && unknownFile) { updateConsumption = true; }

                        if (!fileDesc.previewBlob && fileDesc.isThumbnailPossible()) {
                            try {
                                const blob = await this.fileServerService.getBlobThumbnailFromFileDescriptor(fileDesc);
                                fileDesc.previewBlob = blob;
                                fileDesc.previewBlobSubject.next(fileDesc.previewBlob);
                                this.eventService.publish("ON_FILE_DESCRIPTOR_RECEIVED_EVENT", { fileId: fileDesc.id });
                            } catch (error) {
                                this.logger.error(`[FileDescriptorEventHandler] fileDescriptor retrieve error : ${error}`);
                            }
                        }
                        //else { fileDesc.previewBlobSubject.next(fileDesc.previewBlob); }
                        if (fileDesc.virusScanState === "isClean") {
                            this.fileStorageService.updateAFileDescriptorLocally(fileDesc);
                            this.eventService.publish("ON_FILE_DESCRIPTOR_RECEIVED_EVENT", { fileId: fileDesc.id });
                        }
                        this.fileStorageService.fileDescriptorsSubject.next({ action: 'update', fd: fileDesc });

                        if (fileDesc.state === FileState.NOT_UPLOADED) {
                            //update the file again after few seconds
                            setTimeout(async () => {
                                const file = this.fileStorageService.getFileDescriptorById(fileId);
                                if (file?.state === FileState.NOT_UPLOADED) {
                                    this.logger.info("[FileDescriptorEventHandler] fileDescriptor retrived again to update state");
                                    const fileDesc = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId, true);
                                    this.fileStorageService.fileDescriptorsSubject.next({ action: 'update', fd: fileDesc });
                                }
                            }, 1000);
                        }
                    } catch (error) {
                        this.logger.error(`[FileDescriptorEventHandler] fileDescriptor retrieve error : ${error}`);
                        if (error?.errorDetailsCode === 403001) {
                            // We are no longer viewer of this file
                            // Note: if the same file has been shared with user on another conversation, then we wouldn't be in this case, so there should be no problem to completely delete the file
                            this.fileStorageService.removeFileDescriptorById(fileId);
                            this.eventService.publish("ON_FILE_REMOVED_FROM_STORE_EVENT", { fileId: fileId });
                            this.fileStorageService.sendEvent(FileStorageServiceEvents.ON_FILE_REMOVED_FROM_STORE_EVENT, { fileId: fileId });
                        }
                    }
                    this.fileStorageService.retrieveAndStoreOneVoiceMessageFileDescriptor(fileId);

                    break;
                }
                default:
                    break;
            }
        });

        if (updateConsumption) {
            this.fileStorageService.retrieveUserConsumption();
        }
    }

    public async manageThumbnailStanzaData(stanzaElem: XMLElements) {

        const action = stanzaElem.attr("action");
        const fileId = stanzaElem.find("fileid").text();

        const fileUrl = stanzaElem.find("url").text();
        const fileMime = stanzaElem.find("mime").text();
        const fileSize = stanzaElem.find("size").text();
        const md5sum = stanzaElem.find("md5sum").text();
        const originalWidth = stanzaElem.find("originalWidth").text();
        const originalHeight = stanzaElem.find("originalHeight").text();

        this.logger.info("[FileDescriptorEventHandler] manageThumbnailStanzaData -- " + action + " fileDescriptor " + fileId);
        this.logger.debug("[FileDescriptorEventHandler]   fileUrl=" + fileUrl);
        this.logger.debug("[FileDescriptorEventHandler]   fileMime=" + fileMime);
        this.logger.debug("[FileDescriptorEventHandler]   fileSize=" + fileSize);
        this.logger.debug("[FileDescriptorEventHandler]   md5sum=" + md5sum);
        this.logger.debug("[FileDescriptorEventHandler]   fileId=" + fileId);

        if (action === "create" || action === "update") {
            try {
                await this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId);

                const fileDescriptor = this.fileStorageService.getFileDescriptorById(fileId);
                if (fileDescriptor) {
                    this.logger.debug("[FileDescriptorEventHandler] updating Thumbnail of found FileDescriptor " + fileId);

                    if ((fileDescriptor.previewBlob && !fileDescriptor.previewDownloaded) || !fileDescriptor.previewBlob) {
                        this.eventService.publish("ON_FILE_DESCRIPTOR_THUMBNAIL_READY", fileDescriptor.id);

                        // Either we have the raw file serve as thumbnail or we don't have one yet
                        fileDescriptor.thumbnail.availableThumbnail = true;
                        fileDescriptor.thumbnail.md5sum = md5sum;
                        fileDescriptor.thumbnail.size = Number(fileSize);
                        if (originalWidth && originalHeight) {
                            fileDescriptor.setOriginalImageDimensions(Number(originalWidth), Number(originalHeight));
                        }

                        this.logger.info("[FileDescriptorEventHandler] preview not already downloaded for " + fileId + " -- download it");
                        try {
                            await this.fileServerService.getBlobThumbnailFromFileDescriptor(fileDescriptor);
                            this.eventService.publish("ON_FILE_DESCRIPTOR_RECEIVED_EVENT", { fileId: fileDescriptor.id });
                        } catch (error) {
                            this.logger.error("[FileDescriptorEventHandler] Unable to get blob thumbnail from file descriptor");
                        }
                    } else {
                        this.eventService.publish("ON_FILE_DESCRIPTOR_THUMBNAIL_READY", fileDescriptor.id);
                        this.logger.info("[FileDescriptorEventHandler] preview already downloaded for " + fileId + " -- do nothing");
                    }
                } else {
                    this.logger.error("[FileDescriptorEventHandler] FileDescriptor not found and we do nothing and its not OK");
                }
            } catch (__error) {
                this.logger.error("[FileDescriptorEventHandler] FileDescriptor was not found on server...");
            }
        }
    }

}
